import React from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";

import MarkdownWrapper from "@/components/markdownWrapper";
import MediaWrapper from "@/components/mediaWrapper";
import LinkWrapper from "@/components/linkWrapper";

/*
  This component requires some changes to your config:
  
  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/typography'),
    ],
  }
  ```
*/

export const query = graphql`
  fragment ArticleCenteredArticle on StrapiArticle {
    title
    locale
    date
    categories {
      url
      title
    }
    author {
      url
      name
    }
    readingTimeMinutes
    description
    descriptionShort
    headImage {
      ...StrapiMediaSubquery
    }
    body {
      ...StrapiSharedBody
    }
  }

  fragment ArticleCenteredData on StrapiComponentArticleCentered {
    readingTimeText
    useToc
    tocTitle
    tocIncludedHeadings {
      string
    }
  }
`;

function Centered({ data, articles }) {
  if (!data || !articles?.length) {
    return <></>;
  }
  const articleData = articles[0];

  let articleMetadata = [];
  let category = articleData.categories?.find((c) => c.title && c.url);
  if (category) {
    articleMetadata.push(
      <LinkWrapper
        to={category.url}
        className="underline hover:text-text-secondary-dark"
      >
        <span>{category.title}</span>
      </LinkWrapper>
    );
  }
  if (articleData.author) {
    articleMetadata.push(
      <LinkWrapper
        to={articleData.author.url}
        className="underline hover:text-text-secondary-dark"
      >
        <span>{articleData.author.name}</span>
      </LinkWrapper>
    );
  }
  if (articleData.date && articleData.locale) {
    articleMetadata.push(
      <time
        dateTime={new Date(articleData.date).toLocaleDateString(
          articleData.locale,
          {
            day: "numeric",
            month: "long",
            year: "numeric",
          }
        )}
      >
        {new Date(articleData.date).toLocaleDateString(articleData.locale, {
          day: "numeric",
          month: "long",
          year: "numeric",
        })}
      </time>
    );
  }
  if (articleData.readingTimeMinutes && data.readingTimeText) {
    articleMetadata.push(
      <span>{`${articleData.readingTimeMinutes} ${data.readingTimeText}`}</span>
    );
  }

  return (
    <div className="overflow-hidden bg-background-transparent">
      <div className="mx-auto w-full bg-background-white px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl text-lg">
          {articleMetadata?.length && (
            <span className="block text-center text-base font-semibold uppercase tracking-wide text-text-secondary">
              {articleMetadata.map((metadataSnipped, index) => (
                <span key={index}>
                  {index !== 0 && (
                    <span aria-hidden="true" className="px-1">
                      &middot;
                    </span>
                  )}
                  {metadataSnipped}
                </span>
              ))}
            </span>
          )}
          {articleData.title && (
            <h1 className="mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-text-primary sm:text-4xl">
              <MarkdownWrapper text={articleData.title} />
            </h1>
          )}
          {(articleData.description || articleData.descriptionShort) && (
            <MarkdownWrapper
              text={articleData.description || articleData.descriptionShort}
              markdownClassName="mt-8 text-xl text-text-secondary leading-8"
              replaceParagraph={false}
            />
          )}
          {articleData.headImage && (
            <MediaWrapper
              media={articleData.headImage}
              divClassName={"w-full mt-12 rounded-lg"}
            />
          )}
        </div>
        {articleData.body?.text && (
          <div className="prose-prose-primary prose prose-lg mx-auto mt-12 max-w-5xl text-text-secondary">
            <MarkdownWrapper
              text={articleData.body.text}
              useToc={data.useToc}
              tocTitle={data.tocTitle || "Table of Content"}
              tocOptions={{
                headings: data.tocIncludedHeadings?.map((h) => h.string) || [
                  "h1",
                  "h2",
                ],
                cssClasses: {
                  // cant escape from prose. so cant style toc.
                  toc: "not-prose",
                  list: "not-prose list-none",
                  listItem: "not-prose list-none",
                  link: "not-prose",
                },
              }}
              replaceParagraph={false}
              images={articleData.body.images}
              imgDivClassName="w-full"
              imgClassName="rounded-lg"
            />
          </div>
        )}
      </div>
    </div>
  );
}

Centered.propTypes = {
  data: PropTypes.object,
  articleData: PropTypes.object,
};

export default Centered;
